import { PendingVoziqSaving, VoziqOffer, VoziqOfferContext, VoziqSaving } from './types';

export type ApplyVoziqSaving = (pending: PendingVoziqSaving) => ng.IPromise<any> | Promise<any>;

export const ChangeOfferReason = 'changeOffer';
export const VoziqOffersChangedEvent = 'voziqOffersChanged';
const ShownOffersKey = 'VoziqOffersShown';
const PendingSavingKey = 'VoziqPendingSaving';
const RenewalSignedKey = 'VoziqRenewalSigned';

type SigningResult = {
    saving: VoziqSaving | null;
    applied: boolean;
};

export class VoziqOfferFlow {
    private isOpen = false;

    constructor(private $uibModal: any,
                private $q: ng.IQService,
                private $rootScope: ng.IRootScopeService,
                private dataContext: any,
                private fpStore: any,
                constants: any) {
        'ngInject';
        $rootScope.$on(constants.events.logout, () => this.reset());
        $rootScope.$on(constants.events.loginRequired, () => this.reset());
    }

    public getOffers(): ng.IPromise<VoziqOffer[]> {
        // fpws records the signing asynchronously, so the server still returns the offers right after a signing.
        if (this.fpStore.get(RenewalSignedKey, 'session')) {
            return this.$q.resolve([]);
        }
        return this.dataContext.voziqService.getVoziqOffers();
    }

    public autoOpen(context: VoziqOfferContext, applySaving?: ApplyVoziqSaving): ng.IPromise<void> {
        return this.getOffers().then((offers) => {
            const shown: number[] = this.fpStore.get(ShownOffersKey, 'session') || [];
            if (offers.some((offer) => shown.indexOf(offer.VoziqOfferID) < 0)) {
                return this.open(context, applySaving);
            }
        });
    }

    public open(context: VoziqOfferContext,
                applySaving?: ApplyVoziqSaving,
                selectedOfferId?: number): ng.IPromise<void> {
        if (this.isOpen) {
            return this.$q.resolve();
        }

        this.isOpen = true;
        return this.getOffers()
            .then((offers) => {
                this.fpStore.set(ShownOffersKey, offers.map((offer) => offer.VoziqOfferID), 'session');
                const selectedOffer = offers.filter((offer) => offer.VoziqOfferID === selectedOfferId)[0];
                return selectedOffer ? this.signOffer(selectedOffer, offers) : this.chooseAndSign(offers);
            })
            .then((offer) => offer ? this.complete(offer, context, applySaving) : undefined)
            .finally(() => {
                this.isOpen = false;
            });
    }

    public keepPendingSaving(offer: VoziqOffer, saving: VoziqSaving) {
        this.fpStore.set(PendingSavingKey, this.toPending(offer, saving), 'session');
    }

    public takePendingSaving(): PendingVoziqSaving | null {
        const pending = this.fpStore.get(PendingSavingKey, 'session');
        this.fpStore.remove(PendingSavingKey, 'session');
        return pending || null;
    }

    private reset() {
        this.fpStore.remove(ShownOffersKey, 'session');
        this.fpStore.remove(PendingSavingKey, 'session');
        this.fpStore.remove(RenewalSignedKey, 'session');
        this.dataContext.voziqService.clearVoziqOffers();
    }

    private chooseAndSign(offers: VoziqOffer[]): ng.IPromise<VoziqOffer | null> {
        return this.openModal('fpVoziqOffersModal', { offers: () => offers }, 'md')
            .then((offer: VoziqOffer) => this.signOffer(offer, offers), () => null);
    }

    private signOffer(offer: VoziqOffer, offers: VoziqOffer[]): ng.IPromise<VoziqOffer | null> {
        const shownOfferIds = offers.map((shown) => shown.VoziqOfferID);
        return this.openModal('fpVoziqSignModal', { offer: () => offer, shownOfferIds: () => shownOfferIds }, 'lg')
            .then(() => offer, (reason: any) => reason === ChangeOfferReason ? this.chooseAndSign(offers) : null);
    }

    private complete(offer: VoziqOffer, context: VoziqOfferContext, applySaving?: ApplyVoziqSaving): ng.IPromise<void> {
        this.fpStore.set(RenewalSignedKey, true, 'session');
        this.dataContext.voziqService.clearVoziqOffers();

        return this.unlockSaving(offer)
            .then((saving) => {
                this.$rootScope.$broadcast(VoziqOffersChangedEvent);
                return this.applyOrKeep(offer, saving, applySaving);
            })
            .then((result) => this.openModal('fpVoziqAcceptedModal', {
                offer: () => offer,
                saving: () => result.saving,
                applied: () => result.applied,
                context: () => context
            }, 'md'))
            .catch(() => undefined);
    }

    private unlockSaving(offer: VoziqOffer): ng.IPromise<VoziqSaving | null> {
        if (offer.Kind === 'Rmr') {
            return this.$q.resolve(null);
        }

        return this.$q.resolve(this.dataContext.voziqService.unlockSaving(offer.VoziqOfferID))
            .catch(() => null);
    }

    private applyOrKeep(offer: VoziqOffer,
                        saving: VoziqSaving | null,
                        applySaving?: ApplyVoziqSaving): ng.IPromise<SigningResult> {
        if (!saving || !applySaving) {
            return this.$q.resolve({ saving, applied: false });
        }

        return this.$q.resolve(applySaving(this.toPending(offer, saving)))
            .then(() => ({ saving, applied: true }), () => ({ saving, applied: false }));
    }

    private toPending(offer: VoziqOffer, saving: VoziqSaving): PendingVoziqSaving {
        return {
            saving,
            productIds: (offer.Products || []).map((product) => product.ShopProductId).filter((id) => !!id)
        };
    }

    private openModal(component: string, resolve: any, size: string): ng.IPromise<any> {
        return this.$uibModal.open({
            component,
            resolve,
            size,
            backdrop: 'static'
        }).result;
    }
}
